import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ArrowLeft, RefreshCw, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import Header from '../components/Header';

const Container = styled.div`
  min-height: 100vh;
  background-color: #000;
  color: #fff;
  padding: 80px 20px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1000px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #e50914;
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  color: #aaa;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 1rem;
  padding: 0;
  
  &:hover {
    color: #fff;
  }
`;

const TableContainer = styled.div`
  background: #141414;
  border-radius: 12px;
  border: 1px solid #333;
  overflow: hidden;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 800px; /* Force scroll on small screens */
`;

const Th = styled.th`
  background: #1f1f1f;
  color: #888;
  font-weight: 600;
  text-align: left;
  padding: 12px 16px;
  font-size: 0.85rem;
  border-bottom: 1px solid #333;
`;

const Td = styled.td`
  padding: 16px;
  border-bottom: 1px solid #222;
  color: #ddd;
  font-size: 0.9rem;
  vertical-align: top;
`;

const Badge = styled.span`
  background: #333;
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  margin-right: 6px;
  white-space: nowrap;
`;

const SourceLink = styled.a`
  color: #4a9eff;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  margin-top: 4px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const RefreshBtn = styled.button`
  background: #333;
  color: #fff;
  border: 1px solid #555;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;

  &:hover {
    background: #444;
  }
`;

const BatchResultsPage = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchNews = async () => {
        setLoading(true);
        try {
            const res = await apiService.getFeed('init', 'news');
            if (res.success) {
                setItems(res.data);
            }
        } catch (e) {
            console.error("Failed to fetch news logs", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNews();
    }, []);

    const formatDate = (isoString) => {
        if (!isoString) return '-';
        return new Date(isoString).toLocaleString('ko-KR', {
            month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
        });
    };

    return (
        <>
            <Header />
            <Container>
                <ContentWrapper>
                    <PageHeader>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            <BackBtn onClick={() => navigate('/admin')}>
                                <ArrowLeft size={20} /> Back
                            </BackBtn>
                            <Title>Crowled News Logs</Title>
                        </div>
                        <RefreshBtn onClick={fetchNews}>
                            <RefreshCw size={16} className={loading ? "spin" : ""} /> Refresh
                        </RefreshBtn>
                    </PageHeader>

                    {items.length === 0 && !loading ? (
                        <div style={{ textAlign: 'center', color: '#666', marginTop: 50 }}>
                            수집된 데이터가 없습니다. Admin 페이지에서 크롤링을 실행해주세요.
                        </div>
                    ) : (
                        <TableContainer>
                            <Table>
                                <thead>
                                    <tr>
                                        <Th>Source / Topic</Th>
                                        <Th>Content (AI Summary)</Th>
                                        <Th>Original Link</Th>
                                        <Th>Date</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map(item => (
                                        <tr key={item.id}>
                                            <Td style={{ width: '15%' }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                    <Badge style={{ background: '#e50914' }}>{item.sourceCode || 'Unknown'}</Badge>
                                                    <Badge>{item.topicCode || item.topic}</Badge>
                                                </div>
                                            </Td>
                                            <Td style={{ width: '55%' }}>
                                                {/* Image & Title Layout */}
                                                <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                                                    {item.imageUrl && (
                                                        <img src={item.imageUrl} alt="thumb" style={{
                                                            width: 80, height: 60, objectFit: 'cover', borderRadius: 4, flexShrink: 0,
                                                            border: '1px solid #333'
                                                        }} />
                                                    )}
                                                    <div>
                                                        {item.title && <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: 4, color: '#fff', lineHeight: 1.2 }}>{item.title}</div>}
                                                        <div style={{ fontWeight: 400, color: '#ddd', fontSize: '0.9rem', lineHeight: 1.4 }}>
                                                            {item.text || <span style={{ color: '#666' }}>(No Summary Generated)</span>}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Translation */}
                                                <div style={{ color: '#888', fontSize: '0.85rem', marginTop: 4, marginLeft: item.imageUrl ? 92 : 0 }}>
                                                    {item.translation}
                                                </div>
                                            </Td>
                                            <Td style={{ width: '15%' }}>
                                                {item.originalLink && (
                                                    <SourceLink href={item.originalLink} target="_blank" rel="noopener noreferrer">
                                                        Original Article <ExternalLink size={12} />
                                                    </SourceLink>
                                                )}
                                            </Td>
                                            <Td style={{ width: '15%', color: '#666' }}>
                                                {formatDate(item.createdAt)}
                                            </Td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </TableContainer>
                    )}
                </ContentWrapper>
            </Container>
            <style>{`
                .spin {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </>
    );
};

export default BatchResultsPage;
